import { z } from "zod";
import { Colors, UI_COLOR_DEFAULTS, VEHICLE_PAINT_DEFAULTS, contrastRatio, luminance, } from "./color-registry.js";
import { Scenery, standardScenery } from "./scenery.js";
export { standardScenery } from "./scenery.js";
export const DEFAULT_COLORS = {
    ui: { ...UI_COLOR_DEFAULTS, "--cx-on-bet": "#080b13", "--cx-on-cashout": "#080b13" },
    vehicle: { primary: "#e5173f", secondary: "#ffffff", accent: "#1a1a1a", ...VEHICLE_PAINT_DEFAULTS },
    scene: {
        skyBaseTop: "#14243b",
        skyBaseBottom: "#354c68",
        skyMiddleTop: "#080e20",
        skyMiddleBottom: "#101b30",
        skyUpperTop: "#01030a",
        skyUpperBottom: "#060a14",
        planeSkyStart: "#05070c",
        planeSkyEnd: "#05070c",
        rays: "#0e1424",
        clouds: "#ffffff",
        horizon: "#080b13",
        frame: "#0d1220",
        ground: "#080b13",
        disc: "#101728",
        discEdge: "#223050",
        stars: "#ffffff",
        light: "#53b1fd",
        trail: "#ff6b81",
        curve: "#90f6ff",
        curveFill: "#e5173f",
        curveFillEnd: "#e5173f",
        axis: "#b8cce5",
        axisMinor: "#9aa4b8",
        axisText: "#e8ecf5",
        flame: "#ffd166",
        flameEnd: "#f97316",
        smoke: "#7a7a7a",
        debris: "#e5173f",
    },
};
/** Frozen compatibility geometry; never offered as a creation preset. */
export function inheritedScenery() {
    const value = standardScenery();
    value.profile = "inherited-v1";
    value.plane = { rays: { count: 16, speed: 0.02 }, clouds: true };
    value.rocket = { terrain: true, clouds: true, curve: false, smoke: true };
    value.trail = { style: "points", length: 120, width: 5.76, duration: 600, opacity: 0.5, count: 48 };
    value.curve.fillOpacity = 0.55;
    value.debris = { enabled: true, count: 48, duration: 1100, travel: 240 };
    return value;
}
export const VisualPack = z
    .strictObject({ layout: z.literal("table"), colors: Colors, scenery: Scenery })
    .superRefine((value, ctx) => {
    if (value.scenery.profile === "inherited-v1") {
        if (JSON.stringify(value.scenery) !== JSON.stringify(inheritedScenery()))
            ctx.addIssue({
                code: "custom",
                path: ["scenery"],
                message: "inherited profile is frozen and cannot be edited",
            });
        return;
    }
    try {
        const check = (a, b, minimum, path, names) => {
            const ratio = contrastRatio(a, b);
            if (ratio < minimum)
                ctx.addIssue({
                    code: "custom",
                    path,
                    message: `${names}: contrast ${ratio.toFixed(2)}:1 is below ${minimum}:1; increase foreground/background luminance separation`,
                });
        };
        for (const sky of value.scenery.skies)
            for (const stop of sky.stops) {
                const background = value.colors.scene[stop.color];
                for (const role of ["curve", "axis", "axisText"])
                    check(value.colors.scene[role], background, role === "axisText" ? 4.5 : 3, ["colors", "scene", role], `${role} / ${stop.color}`);
                if (value.scenery.outline.enabled)
                    check(value.colors.vehicle.outline, background, 3, ["colors", "vehicle", "outline"], `outline / ${stop.color}`);
            }
        const ui = value.colors.ui;
        for (const surface of [
            "--cx-bg",
            "--cx-surface",
            "--cx-surface-2",
            "--cx-surface-3",
            "--cx-surface-4",
        ]) {
            for (const text of ["--cx-text", "--cx-text-2", "--cx-text-muted"])
                check(ui[text], ui[surface], 4.5, ["colors", "ui", text], `${text} / ${surface}`);
        }
        for (const button of ["bet", "cashout"])
            for (const end of ["start", "end"])
                check(ui[`--cx-on-${button}`], ui[`--cx-${button}-${end}`], 4.5, ["colors", "ui", `--cx-on-${button}`], `on-${button} / ${button}-${end}`);
        let previous = value.scenery.skies[0].stops.map((stop) => luminance(value.colors.scene[stop.color]));
        for (const sky of value.scenery.skies.slice(1)) {
            const next = sky.stops.map((stop) => luminance(value.colors.scene[stop.color]));
            if (Math.max(...next) > Math.min(...previous) * 0.2)
                ctx.addIssue({
                    code: "custom",
                    path: ["scenery", "skies"],
                    message: "each upper sky must be darker: maximum luminance <= 20% of the preceding minimum",
                });
            previous = next;
        }
    }
    catch (error) {
        ctx.addIssue({ code: "custom", path: ["colors", "scene"], message: String(error) });
    }
});
export function parseVisualPack(value) {
    return VisualPack.parse(value);
}
/** Trust comes from the installed built-in catalogue, never from pack-controlled metadata. */
export function loadVisualPack(value, options = {}) {
    if (value &&
        typeof value === "object" &&
        "scenery" in value &&
        value.scenery &&
        typeof value.scenery === "object" &&
        "profile" in value.scenery &&
        value.scenery.profile === "inherited-v1" &&
        !options.builtin)
        throw new Error("Inherited profile cannot be used for creation or duplication; choose standard scenery");
    return parseVisualPack(value);
}
//# sourceMappingURL=visual-pack.js.map